<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { isNumber } from 'lodash-es';
//#endregion

interface Props {
  width?: string | number;
  isActiveButton?: boolean;
  hasFilters: boolean;
  disable?: boolean;
}

interface  Emits {
  open: [];
  apply: [];
  reset: [];
};

const props = withDefaults(defineProps<Props>(), {
  width: 520,
  isActiveButton: true,
  disable: false,
});

const emit = defineEmits<Emits>();

//#region const

//#endregion

//#region data
const active = ref<boolean>(false);
//#endregion

//#region computed
const pWidth = computed(() => {
  return isNumber(props.width)
    ? `${props.width}px`
    : props.width;
});
//#endregion

//#region methods
function handleVisibleChange() {
  active.value = !active.value;
  if(active.value) {
    emit('open');
  }
}

function handleReset() {
  emit('reset');
  if(active.value) {
    handleVisibleChange();
  }
}

function handleApply() {
  handleVisibleChange();
  emit('apply');
}

function handleOutside(e: MouseEvent) {
  const isInsideElPopper = (e.target as Element)?.closest('.el-popper');
  const isPopupInFilter = (e.target as Element)?.closest('.popup-inside-filter');
  if (!isInsideElPopper && active.value && !isPopupInFilter) {
    handleVisibleChange();
  }
}
//#endregion
</script>

<template>
  <div class="ui-filter-element">
    <ActionIcon
      v-if="hasFilters"
      :size="16"
      icon="circle-xmark"
      :is-overlay="false"
      color="var(--text-negative)"
      custom-class="ui-filter-element__clear"
      @click.stop="handleReset"
    />
    <ElPopover
      :visible="active"
      popper-class="ui-filter-element__popper"
      placement="bottom-start"
      :width="pWidth"
      :persistent="true"
      :teleported="false"
    >
      <template v-slot:reference>
        <UiButton
          v-click-outside="handleOutside"
          icon="filter"
          :filled="active || hasFilters"
          @click="handleVisibleChange"
        >
          {{ $t('ui.filters.inner.filter') }}
        </UiButton>
      </template>
      <div class="ui-filter-element__menu">
        <UiScrollbar
          wrap-style="max-height: 75vh; height: auto;"
          :native="false"
        >
          <slot />
        </UiScrollbar>

        <div
          v-if="!disable"
          class="ui-filter-element__controls t:space-x-8"
        >
          <UiButton
            type="negative"
            icon="times"
            @click="handleReset"
          >
            {{ $t('ui.table.reset') }}
          </UiButton>
          <UiButton
            :disabled="!isActiveButton"
            filled
            icon="check"
            @click="handleApply"
          >
            {{ $t('ui.table.apply') }}
          </UiButton>
        </div>
      </div>
    </ElPopover>
  </div>
</template>

<style lang="scss">
.ui-filter-element {
  position: relative;
  &__clear {
    position: absolute;
    right: -8px;
    top: -8px;
    z-index: 1;
    cursor: pointer;
    background: #fff;
    border-radius: 50%;
  }
  &__popper.el-popper {
    padding: 0;
    background-color: #fff;
    box-shadow: 0 4px 16px #0003;
    border-radius: 8px;
    z-index: 800 !important;
  }
  &__menu {
    padding: 8px 14px 16px;
    max-height: initial;
  }
  &__controls {
    border-top: 1px solid var(--border-divider);
    padding-top: 16px;
    display: flex;
    justify-content: flex-end;
    margin: 0 10px;
  }
}
</style>
